<template>
  <div class="usage-container">
    <SummarySkeletonLoader :is-loading="pageLoading || listLoading" />
    <MetricsSummary
      v-if="!pageLoading && !listLoading"
      height="140px"
      class="mb-3"
      :summaries="summaries"
      new-version
    />
    <UsagePanelSkeletonLoader :is-loading="pageLoading || listLoading" />
    <div
      v-if="!pageLoading && !listLoading"
      class="usage-card revisions mb-3"
    >
      <UsagePostRevisions />
      <UsageEngagement />
    </div>
    <UsageJobs />
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import MetricsSummary from '@/components/shared/MetricsSummary.vue';
import { numberWithCommas } from '@/helpers/formatters';
import UsagePostRevisions from '@/components/settings/usage/UsagePostRevisions.vue';
import UsageEngagement from '@/components/settings/usage/UsageEngagement.vue';
import UsageJobs from '@/components/settings/usage/UsageJobs.vue';
import SummarySkeletonLoader from '@/components/shared/SkeletonLoaders/SummarySkeletonLoader.vue';
import UsagePanelSkeletonLoader from '@/components/shared/SkeletonLoaders/usage/UsagePanelSkeletonLoader.vue';
import { getQueryParamsFromURL } from '@/helpers/routing';
import { useAppLoading } from '@/stores/app/loading';
import { useAppAuthData } from '@/stores/app/authData';
import { useUsage } from '@/stores/insights/usage';
import { useInsights } from '@/stores/insights/insights';
import { useAppBase } from '@/stores/app/base';
import { useFilters } from '@/stores/filters/filters';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { useSubMenu } from '@/stores/filters/subMenu';
import { useColors } from '@/composables/colors';
import { useInsightsExport } from '@/stores/insights/export';
import { assignUsersToCSVDownloadLink } from '@/helpers/fileDownload';
import { useDatePicker } from '@/stores/filters/datePicker';
import { useInsightsShare } from '@/stores/insights/share';

export default {
  name: 'UsageMetrics',
  components: {
    MetricsSummary,
    UsagePostRevisions,
    UsageEngagement,
    UsageJobs,
    SummarySkeletonLoader,
    UsagePanelSkeletonLoader,
  },
  emits: ['track-page-load'],
  setup() {
    const { brandPrimary, blue500, green500 } = useColors();

    return { brandPrimary, blue500, green500 };
  },
  data() {
    return {
      preambleKey: 'usage-report-preamble',
    };
  },
  computed: {
    ...mapState(useAppLoading, ['pageLoading', 'listLoading']),
    ...mapState(useAppAuthData, ['authData']),
    ...mapState(useUsage, ['engagementUsers', 'summary', 'jobs']),
    ...mapState(useFilters, ['searchTerm', 'myJobsEnabled', 'mergedTempFilters']),
    ...mapState(useSubMenu, ['selectedSubMenuOption']),
    ...mapState(useAppBase, ['browserBackOrForwardNavigation']),
    ...mapState(useDatePicker, ['dateRange']),
    projectName() { return this.authData.projectName; },
    summaryStaticValues() {
      return {
        avgScore: {
          label: 'Average Score',
          tooltipTitle: 'Performance Score:',
          tooltip: 'Jobs that score 85+ are proven to attract more qualified and diverse candidate pools.',
        },
        avgScoreChange: {
          label: 'Average Score Increase',
          tooltip: 'The average difference between a job post\'s highest and lowest score during the filtered time period.',
        },
        totalJobPosts: {
          label: 'Total Posts Edited',
          tooltip: 'Total number of posts edited that match the selected filters.',
        },
        totalRevisions: {
          label: 'Total Revisions',
          tooltip: `Total number of revisions ${this.projectName || 'your company'} has made to the job posts listed below.`,
        },
        totalUsers: {
          label: 'Total Users',
          tooltip: 'Total number of users who edited posts that match the selected filters.',
        },
      };
    },
    sortedKeys() {
      const { summary } = this;
      const summaryKeys = Object.keys(summary);
      const staticSummaryKeys = Object.keys(this.summaryStaticValues);

      return summaryKeys.sort((aKey, bKey) => {
        const aStaticIndex = staticSummaryKeys.findIndex((key) => key === aKey);
        const bStaticIndex = staticSummaryKeys.findIndex((key) => key === bKey);

        return aStaticIndex - bStaticIndex;
      });
    },
    summaries() {
      if (this.summary) {
        return this.sortedKeys.map((key) => {
          const staticValues = this.summaryStaticValues[key];

          return {
            id: `usage-${key}`,
            label: staticValues?.label || '',
            value: this.getSummaryValue(key),
            color: this.getSummaryColor(key),
            tooltip: {
              title: staticValues?.tooltipTitle || '',
              html: staticValues?.tooltip || '',
              key: `usage-${key}`,
            },
          };
        });
      }
      return [];
    },
    usageExportConfig() {
      return {
        csvExport: true,
        pngExport: false,
        pngWidgetOptions: {},
        csvOptions: [
          {
            isEmpty: !this.jobs || this.jobs.length === 0,
            label: 'Posts Edited (.csv)',
            csvDownloadFunction: async () => this.downloadPlatformUsage({
              document: window.document,
              dateRange: this.dateRange,
              myJobsEnabled: this.myJobsEnabled,
              mergedTempFilters: this.mergedTempFilters,
            }),
          },
          {
            isEmpty: !this.engagementUsers || this.engagementUsers.length === 0,
            label: 'Engagement Data (.csv)',
            csvDownloadFunction: async () => assignUsersToCSVDownloadLink({
              engagementUsers: this.engagementUsers,
              dateRange: this.dateRange,
              projectName: this.projectName,
            }),
          },
        ].filter((option) => !option.isEmpty),
      };
    },
  },
  watch: {
    async browserBackOrForwardNavigation(newValue) {
      /**
       * Verifies the correct path in case this is triggered
       * on a back button click to a different page
       */
      if (newValue && window.location.pathname === '/app/insights/usage') {
        const searchParams = getQueryParamsFromURL();

        await this.loadPage({
          routeQuery: searchParams,
          router: this.$router,
          page: searchParams.page,
          selectedMyJobs: searchParams.owned === 'true',
          searchTerm: this.searchTerm,
          selectedSubMenuOption: this.selectedSubMenuOption,
        });
      }
    },
    usageExportConfig: {
      handler(newValue) {
        this.setExportConfig(newValue);
      },
      deep: true,
    },
  },
  async mounted() {
    subscribeToFilterUpdates(this.loadPage);

    this.setFilterConfig({
      module: 'insights',
      datepicker: true,
      showMyJobs: true,
      subMenuType: null,
    });

    this.showFilterBarSettings({ route: this.$route.name, options: [] });

    this.setShareConfig({
      shareable: true,
    });

    this.setExportConfig(this.usageExportConfig);

    await this.loadPage({
      routeQuery: this.$route.query || '',
      router: this.$router,
      firstLoad: true,
      page: this.$route.query?.page || '',
      selectedMyJobs: this.$route.query?.owned === 'true',
      searchTerm: this.searchTerm,
      selectedSubMenuOption: this.selectedSubMenuOption,
    });

    this.$emit('track-page-load', { page: 'Usage Report' });
  },
  beforeUnmount() {
    unsubscribeToFilterUpdates();

    this.clearPersistentFilters();

    this.resetExportConfig();

    this.resetShareConfig();
  },
  methods: {
    ...mapActions(useInsights, ['loadPage']),
    ...mapActions(useFilters, ['clearPersistentFilters', 'setFilterConfig']),
    ...mapActions(useAppBase, ['showFilterBarSettings']),
    ...mapActions(useInsightsExport, ['setExportConfig', 'resetExportConfig']),
    ...mapActions(useInsightsShare, ['setShareConfig', 'resetShareConfig']),
    ...mapActions(useUsage, ['downloadPlatformUsage']),
    getSummaryValue(summaryKey) {
      const value = this.summary[summaryKey] || 0;
      if (summaryKey === 'avgScoreChange') {
        let prepend = '';
        if (value > 0) {
          prepend = '+';
        } else if (value < 0) {
          prepend = '-';
        }

        return prepend + numberWithCommas(value);
      }
      return numberWithCommas(value);
    },
    getSummaryColor(summaryKey) {
      switch (summaryKey) {
        case 'avgScore':
          return this.blue500;
        case 'avgScoreChange':
          return this.green500;
        case 'totalJobPosts':
          return this.brandPrimary;
        case 'totalRevisions':
          return this.brandPrimary;
        case 'totalUsers':
          return this.brandPrimary;
        default:
          return this.brandPrimary;
      }
    },
  },
};
</script>

<style lang="less" scoped>

.usage-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.v-card-header {
    background-color: transparent;
    box-shadow: none !important;
  }

.statistic-summary {
  height: 136px;
}

.revisions {
  height: 336px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}
</style>
